/** Overrides exclusivos de presentación WEB. NULL conserva la plantilla original. */
export interface ConfiguracionTiqueteWeb {
  version: 1;
  fuente: 'actual';
  perfil_tamano: 'actual' | 'compacto' | 'normal' | 'grande';
  interlineado: number | null;
  espacio_encabezado: number | null;
  espacio_antes_detalle: number | null;
  espacio_despues_detalle: number | null;
  espacio_antes_total: number | null;
  espacio_antes_pie: number | null;
  espacio_entre_mensajes: number | null;
  espacio_final: number | null;
}

export const CONFIGURACION_WEB_ACTUAL: ConfiguracionTiqueteWeb = {
  version: 1, fuente: 'actual', perfil_tamano: 'actual', interlineado: null,
  espacio_encabezado: null, espacio_antes_detalle: null, espacio_despues_detalle: null,
  espacio_antes_total: null, espacio_antes_pie: null, espacio_entre_mensajes: null,
  espacio_final: null,
};

export type PresetEspacioWeb = 'minimo' | 'normal' | 'amplio';
export type GrupoEspacioWeb = 'secciones' | 'lineas' | 'final';
const CAMPOS_SECCIONES = ['espacio_encabezado', 'espacio_antes_detalle', 'espacio_despues_detalle', 'espacio_antes_total', 'espacio_antes_pie', 'espacio_entre_mensajes'] as const;

/** Presets de interfaz: persisten en el contrato numérico existente, sin migraciones. */
export function aplicarPresetEspacioWeb(config: ConfiguracionTiqueteWeb | null, grupo: GrupoEspacioWeb, preset: PresetEspacioWeb, ancho: 58 | 80): ConfiguracionTiqueteWeb {
  const salida = {...(config || CONFIGURACION_WEB_ACTUAL)};
  const indice = ['minimo', 'normal', 'amplio'].indexOf(preset);
  if (grupo === 'lineas') salida.interlineado = [1.15, 1.25, 1.4][indice];
  else if (grupo === 'final') salida.espacio_final = (ancho === 58 ? [1, 3, 6] : [1.5, 4, 8])[indice];
  else for (const campo of CAMPOS_SECCIONES) salida[campo] = (ancho === 58 ? [0, 1, 2] : [0, 1.5, 3])[indice];
  return salida;
}

/** No aproxima ni transforma configuraciones antiguas personalizadas. */
export function reconocerPresetEspacioWeb(config: ConfiguracionTiqueteWeb | null, grupo: GrupoEspacioWeb, ancho: 58 | 80): PresetEspacioWeb | null {
  if (!config) return null;
  const campos = grupo === 'secciones' ? CAMPOS_SECCIONES : grupo === 'lineas' ? ['interlineado'] as const : ['espacio_final'] as const;
  for (const preset of ['minimo', 'normal', 'amplio'] as const) {
    const esperado = aplicarPresetEspacioWeb(null, grupo, preset, ancho);
    if (campos.every(campo => config[campo] === esperado[campo])) return preset;
  }
  return null;
}

/** Evita líneas sueltas en etiquetas y hora; exclusivo del HTML WEB. */
export function compactarMetadatosWeb(html: string): string {
  html = html.replace(/(<span class="meta-value(?:58)?">)([^<]*)(<\/span>)/g, (original, apertura, texto, cierre) => {
    const fecha = texto.match(/^(.*?)(\d{1,2}:\d{2}(?::\d{2})?)(\s*[ap]\.\s*m\.?)?$/i);
    if (!fecha) return original;
    const periodo = fecha[3] ? fecha[3].replace(/[.\s]/g, '') : '';
    return `${apertura}${fecha[1]}<span class="hora-web">${fecha[2]}${periodo ? ` ${periodo}` : ''}</span>${cierre}`;
  });
  return html.replace('</style>', '\n.meta-row,.meta-row58{grid-template-columns:max-content minmax(0,1fr);}\n.meta-label,.meta-label58,.hora-web{white-space:nowrap;overflow-wrap:normal;}\n</style>');
}

/** Valores desconocidos nunca se interpolan como CSS. */
export function normalizarConfiguracionWeb(valor: unknown): ConfiguracionTiqueteWeb | null {
  if (!valor || typeof valor !== 'object') return null;
  const entrada = valor as Record<string, unknown>;
  if (entrada.version !== 1 || entrada.fuente !== 'actual' ||
      !['actual', 'compacto', 'normal', 'grande'].includes(String(entrada.perfil_tamano))) return null;
  const salida = { ...CONFIGURACION_WEB_ACTUAL, perfil_tamano: entrada.perfil_tamano } as ConfiguracionTiqueteWeb;
  for (const campo of Object.keys(CONFIGURACION_WEB_ACTUAL)) {
    if (campo === 'interlineado' || campo.startsWith('espacio_')) {
      const numero = entrada[campo];
      if (numero == null) continue;
      const minimo = campo === 'interlineado' ? 1.15 : 0;
      const maximo = campo === 'interlineado' ? 1.4 : campo === 'espacio_final' ? 12 : campo === 'espacio_entre_mensajes' ? 4 : 6;
      if (typeof numero !== 'number' || !Number.isFinite(numero) || numero < minimo || numero > maximo) return null;
      Object.assign(salida, { [campo]: numero });
    }
  }
  return salida;
}

/** Mantiene medidas y HTML originales; añade únicamente overrides explícitos. */
export function aplicarConfiguracionWeb(html: string, valor: unknown): string {
  const config = normalizarConfiguracionWeb(valor);
  if (!config) return html;
  const escala = { actual: 1, compacto: 0.9, normal: 1, grande: 1.12 }[config.perfil_tamano];
  const reglas: string[] = [];
  if (config.perfil_tamano !== 'actual') {
    // Tomar métricas de cada plantilla conserva sus proporciones. Los avisos legales permanecen intactos.
    const estilo = html.match(/<style>([\s\S]*?)<\/style>/)?.[1] || '';
    for (const bloque of estilo.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
      const selector = bloque[1].trim();
      const tamano = bloque[2].match(/font-size:\s*([\d.]+)px/);
      if (tamano && !selector.includes('anulad') && !selector.includes('@')) {
        reglas.push(`${selector}{font-size:${(Number(tamano[1]) * escala).toFixed(2)}px!important;}`);
      }
    }
    // Importe completo, incluso cuando es más ancho que su columna; nunca se trunca.
    reglas.push('.monto,.monto58,.rev-monto58,.rev-col{min-width:0;max-width:100%;white-space:normal;overflow-wrap:anywhere;} .nums,.nums58,.meta-value,.meta-value58,.rev-nums,.local,.title,.sorteo,.sorteo-destacado,.total,.nota,.center-note,.ticket-id{min-width:0;max-width:100%;overflow-wrap:anywhere;}');
  }
  if (config.interlineado !== null) reglas.push(`.ticket,.ticket *{line-height:${config.interlineado}!important;}`);
  const espacios: Array<[keyof ConfiguracionTiqueteWeb, string, string]> = [
    ['espacio_encabezado', '.meta-box,.meta-box58', 'margin-top'],
    ['espacio_antes_detalle', '.meta-box + .spacer,.meta-box58 + .spacer58', 'height'],
    ['espacio_despues_detalle', '.detalle-contenido-web', 'margin-bottom'],
    ['espacio_antes_total', '.total', 'margin-top'],
    ['espacio_antes_pie', '.center-note,.nota', 'margin-top'],
    ['espacio_entre_mensajes', '.center-note + .line,.nota + .ticket-id', 'margin-top'],
    ['espacio_final', '.ticket', 'padding-bottom'],
  ];
  for (const [campo, selector, propiedad] of espacios) {
    if (config[campo] !== null) reglas.push(`${selector}{${propiedad}:${config[campo]}mm!important;}`);
  }
  if (!reglas.length) return html;
  html = html.replace(/(<div class="detalle(?:58)?">DETALLE<\/div>)([\s\S]*?)(<hr class="sep"\/>\s*<div class="total">)/, '$1<div class="detalle-contenido-web">$2</div>$3');
  // La captura WEB extrae el primer bloque CSS: incluir ahí los overrides evita duplicar plantilla.
  return html.replace('</style>', `\n/* data-tiquete-web=1 */\n${reglas.join('\n')}\n</style>`);
}
